import React, { Component, ReactNode } from "react";

import { IDeviceStreamConfiguration } from "@formant/core/model/IDeviceStreamConfiguration";
import { defaultROSVersion } from "@formant/core/model/rosVersions";

import { Buttons } from "../common/Buttons";
import { Help } from "../common/Help";
import { StandardButton } from "../common/StandardButton";
import * as styles from "./EditStreamHeader.scss";

export interface IEditStreamHeaderProps {
    stream: IDeviceStreamConfiguration;
    onAdd: (
        stream: IDeviceStreamConfiguration,
        replacesStream?: IDeviceStreamConfiguration,
    ) => void;
}

export class EditStreamHeader extends Component<IEditStreamHeaderProps> {
    public render() {
        const { stream } = this.props;

        let header: ReactNode = null;
        switch (stream.configuration.type) {
            case "directory-watch":
                header = (
                    <p>
                        Each file created within the directory will be ingested
                        as a telemetry stream data point.
                        <Help
                            text="Click for docs."
                            to="https://docs.formant.io/docs/getting-started-add-a-directory-watch-stream"
                        />
                    </p>
                );
                break;
            case "file-tail":
                header = (
                    <>
                        <p>
                            Each line appended to the specified file is ingested
                            as a <code>text</code> data point.
                            <Help
                                text="Click for docs."
                                to="https://docs.formant.io/docs/getting-started-add-a-file-tail-stream"
                            />
                        </p>
                        <Buttons className={styles.buttons}>
                            <StandardButton
                                className={styles["add-stream"]}
                                context={stream.name}
                                onClicked={this.addFileTail}
                            >
                                Add file
                            </StandardButton>
                        </Buttons>
                    </>
                );
                break;
            case "ros-topic":
                header = (
                    <>
                        <p>
                            Create a stream by adding one or more ROS topics.
                            Click 'Add ROS Topic' to add more topics.
                            <Help
                                text="Click for docs."
                                to="https://docs.formant.io/docs/getting-started-add-a-ros-topic-stream"
                            />
                        </p>
                        <Buttons className={styles.buttons}>
                            <StandardButton
                                className={styles["add-stream"]}
                                context={stream.name}
                                onClicked={this.addRosTopic}
                            >
                                Add ROS topic
                            </StandardButton>
                        </Buttons>
                    </>
                );
                break;
            case "ros-localization":
                header = (
                    <p>
                        Configure map, odometry, pointcloud, path, and goal
                        topics.
                        <Help
                            text="Click for docs."
                            to="https://docs.formant.io/docs/getting-started-add-a-ros-localization-stream"
                        />
                    </p>
                );
                break;
            case "ros-transform-tree":
                header = (
                    <p>
                        Ingest <code>/tf</code>, <code>/tf_static</code> topics
                        as a telemetry stream.
                        <Help
                            text="Click for docs."
                            to="https://docs.formant.io/docs/getting-started-add-a-ros-transform-tree-stream"
                        />
                    </p>
                );
                break;
            case "custom":
                header = (
                    <p>
                        Configure streams which are ingested via the Agent SDK.
                        <br />
                        Ingested streams will be auto-configured based on
                        detected data type. Use this window to configure stream
                        rate, on-demand, or encoding.
                        <Help
                            text="Click for docs."
                            to="https://docs.formant.io/docs/getting-started-add-a-non-ros-data-stream"
                        />
                    </p>
                );
                break;
            case "hardware":
                header = (
                    <p>
                        Configure a stream directly from a camera.
                        <Help
                            text="Click for docs."
                            to="https://docs.formant.io/docs/getting-started-add-a-video-stream"
                        />
                    </p>
                );
        }

        return <div className={styles["edit-stream-header"]}>{header}</div>;
    }

    private addRosTopic = (streamName: string) => {
        const { onAdd } = this.props;
        onAdd({
            name: streamName,
            configuration: {
                type: "ros-topic",
                topic: "",
                rosVersion: defaultROSVersion,
            },
        });
    };

    private addFileTail = (streamName: string) => {
        const { onAdd } = this.props;
        onAdd({
            name: streamName,
            configuration: {
                type: "file-tail",
                filename: "",
            },
        });
    };
}
